'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { register, updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import type { LoginResponse } from '@eliadmin/core/src/api/customers/customers-types';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import Link from '@eliadmin/core/src/components/link';
import {
  FormResultMessage,
  type FormResult,
} from '@eliadmin/core/src/components/nt/account/shell/form-result';
import { applyServerErrors } from '@eliadmin/core/src/components/nt/account/shell/server-errors';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { RichText } from '@eliadmin/core/src/components/nt/ui/rich-text';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

import { AccountTypeSwitch } from './account-type-switch';
import {
  BillingCard,
  CompanyCard,
  ContactPersonCard,
  CredentialsCard,
  DeliveryCard,
  PersonalCard,
} from './register-cards';
import { RegisterCheckbox } from './register-fields';
import {
  REGISTER_SERVER_FIELDS,
  registerSchema,
  type RegisterFormInput,
  type RegisterFormValues,
} from './register-schema';
import { registerDefaults, toCustomerUpdate, toRegisterBody } from './register-values';
import type { RegisteredAccount, RegisterTexts } from './types';

/**
 * The registration form under the H1 (Figma 221:13538 person, 221:13989 company): „Registrujem sa
 * ako“ with the switch, the cards of the chosen persona, the consents, „Vytvoriť účet“ and the link
 * to the sign-in. The submit is the core registration (`register`, then the token cookie and the
 * customer store exactly like the core form) followed – with the new token – by `updateCustomer`
 * for the data the register request cannot carry (company, addresses). A 422 of the registration
 * goes onto its fields; once the account exists the success state follows even if the update
 * fails (the data can be completed in the profile – nothing claims they were saved).
 */
export function RegisterForm({
  texts,
  countries,
  returnUrl,
  onRegistered,
}: {
  texts: RegisterTexts;
  countries: Country[];
  returnUrl?: string;
  onRegistered: (account: RegisteredAccount) => void;
}) {
  const { t } = useTranslations();
  const accountTexts = useAccountTexts();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(s => s.currentLocale);
  const layoutCountry = useGlobalStore(s => s.generalLayout.country?.id);
  const defaultCountry = countries.some(country => country.id === layoutCountry)
    ? (layoutCountry ?? '')
    : (countries[0]?.id ?? '');
  const typeId = useId();
  const [result, setResult] = useState<FormResult>(null);

  const form = useForm<RegisterFormInput, unknown, RegisterFormValues>({
    resolver: zodResolver(
      registerSchema(
        formValidationMessages,
        accountTexts.passwordRules,
        texts.streetNumberMissing || undefined,
      ),
    ),
    mode: 'onTouched',
    defaultValues: registerDefaults(defaultCountry),
  });
  const [company, otherDelivery] = useWatch({
    control: form.control,
    name: ['company_buy', 'other_delivery'],
  });

  const onSubmit = form.handleSubmit(async values => {
    setResult(null);
    let registered: LoginResponse['data'];
    try {
      registered = (await register(toRegisterBody(values, locale))).data;
    } catch (error) {
      const rest = applyServerErrors(error, form.setError, field => REGISTER_SERVER_FIELDS[field]);
      if (rest) setResult({ status: 'failed', message: rest.join('\n') || t('text653') });
      return;
    }

    // From here on the account exists: whatever fails below, the success state follows and the
    // profile can complete the data (nothing on it claims the addresses were saved).
    const authToken = registered.token;
    let customer = registered.customer;
    await setAuthToken({ authToken }).catch(() => undefined);
    const update = toCustomerUpdate(values, countries);
    if (update) {
      try {
        const { data } = await updateCustomer({ locale, authToken, data: update });
        customer = { ...customer, ...data };
      } catch {
        // Kept as registered – the store has the name, e-mail and phone already.
      }
    }
    initCustomerStore({ authToken, customer });
    onRegistered({
      email: customer.email || values.email,
      customerId: customer.id,
      verified: registered.verified,
    });
  });

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6 md:gap-10">
        <div className="flex flex-col gap-3.5 sm:gap-4">
          <h2 id={typeId} className="nt-h5 text-[#0D110D]">
            {texts.as}
          </h2>
          <AccountTypeSwitch labelledBy={typeId} companyLabel={texts.company} />
        </div>

        <div className="flex flex-col gap-4">
          <CredentialsCard texts={texts} />
          {company ? (
            <>
              <CompanyCard countries={countries} texts={texts} />
              {otherDelivery ? <DeliveryCard company countries={countries} texts={texts} /> : null}
              <ContactPersonCard texts={texts} />
            </>
          ) : (
            <>
              <PersonalCard texts={texts} />
              <BillingCard countries={countries} texts={texts} />
              {otherDelivery ? (
                <DeliveryCard company={false} countries={countries} texts={texts} />
              ) : null}
            </>
          )}
          {/* Figma „Súhlasy“: 8 px more above, 12 px apart (8 on the phone). */}
          <div className="flex flex-col gap-2 pt-2 sm:gap-3">
            <RegisterCheckbox
              name="consent"
              required
              label={
                <>
                  <RichText as="span" html={texts.consent} variant="nt-body-l" links="inherit" />
                  <span aria-hidden="true"> *</span>
                </>
              }
            />
            {texts.newsletter ? (
              <RegisterCheckbox name="newsletter" label={texts.newsletter} />
            ) : null}
          </div>
        </div>

        {/* Figma submit block: the button (full width on the phone), 24 px (16) above the link. */}
        <div className="flex flex-col items-center gap-4 sm:gap-6">
          <FormResultMessage result={result} className="empty:-mt-4 sm:empty:-mt-6" />
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            className="w-full sm:w-auto"
          >
            {t('text758')}
          </Button>
          <p className="nt-body-m flex flex-wrap justify-center gap-x-1.5 text-[#363D36]">
            {texts.haveAccount || t('text3412')}
            <Link
              href={ROUTES.SIGN_IN(returnUrl) ?? '#'}
              prefetch={false}
              className="nt-body-m-bold rounded text-[#0E6A20] underline underline-offset-2 transition-colors hover:text-[#0B5719] focus-visible:shadow-focus focus-visible:outline-none"
            >
              {t('text357')}
            </Link>
          </p>
        </div>
      </form>
    </FormProvider>
  );
}
